import { forwardRef, type SVGProps } from 'react'
import {
  Alert02Icon,
  AlertCircleIcon as AlertCircleIconSvg,
  BarChartHorizontalIcon as BarChartHorizontalIconSvg,
  Briefcase01Icon,
  Building06Icon,
  BulbIcon,
  ChartBarBigIcon as ChartBarBigIconSvg,
  ChartUpIcon,
  CheckmarkCircle01Icon,
  DashboardSquare01Icon,
  File01Icon,
  HelpCircleIcon as HelpCircleIconSvg,
  RadarIcon as RadarIconSvg,
  SortByDown01Icon,
  SparklesIcon as SparklesIconSvg,
  Store01Icon,
  JobSearchIcon,
  Settings02Icon,
  TargetIcon as TargetIconSvg,
  UserCircle02Icon,
  UserGroupIcon,
  UserIcon,
  UserMultiple02Icon,
  UserSearch01Icon,
} from '@hugeicons/core-free-icons'
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react'

/**
 * Creates a React component from a hugeicon definition that matches the LucideIcon API.
 * This lets consumers use `<Icon className="..." />` without knowing about HugeiconsIcon.
 */
function createIcon(icon: IconSvgElement) {
  return forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>((props, ref) => (
    <HugeiconsIcon ref={ref} icon={icon} {...props} strokeWidth={2} />
  ))
}

/**
 * Hugeicons-backed icons that match the lucide icon API (className/size pass through), exported under the
 * same names as their lucide counterparts so call sites only swap the import source. Use these instead of
 * importing from 'lucide-react' so report section icons stay consistent with the hugeicons icon set.
 */
export const SparklesIcon = createIcon(SparklesIconSvg)
export const RadarIcon = createIcon(RadarIconSvg)
export const BarChartHorizontalIcon = createIcon(BarChartHorizontalIconSvg)
export const ChartBarBigIcon = createIcon(ChartBarBigIconSvg)
export const ArrowDownWideNarrowIcon = createIcon(SortByDown01Icon)
export const CheckCircleIcon = createIcon(CheckmarkCircle01Icon)
export const AlertTriangleIcon = createIcon(Alert02Icon)
export const AlertCircleIcon = createIcon(AlertCircleIconSvg)
export const HelpCircleIcon = createIcon(HelpCircleIconSvg)
export const TargetIcon = createIcon(TargetIconSvg)
export const LightbulbIcon = createIcon(BulbIcon)
export const TrendingUpIcon = createIcon(ChartUpIcon)

/**
 * Shared navigation and profile type icon configuration.
 * Uses hugeicons (stroke-rounded) for a clean, consistent icon style.
 * This ensures consistency across app-topbar, avatars, profile selectors, and page title sections.
 */
export const NAVIGATION_ICONS = {
  dashboard: createIcon(DashboardSquare01Icon),
  organizations: createIcon(Building06Icon),
  settings: createIcon(Settings02Icon),

  // Profile type icons (used in avatars and profile selectors)
  employee: createIcon(UserIcon),
  employees: createIcon(UserIcon),
  applicant: createIcon(UserCircle02Icon),
  applicants: createIcon(UserCircle02Icon),
  job: createIcon(Briefcase01Icon),
  jobs: createIcon(Briefcase01Icon),
  offer: createIcon(JobSearchIcon),
  offers: createIcon(JobSearchIcon),
  team: createIcon(UserGroupIcon),
  teams: createIcon(UserGroupIcon),
  teamMembers: createIcon(UserMultiple02Icon),
  orgGroup: createIcon(UserGroupIcon),
  market: createIcon(Store01Icon),
  marketOffer: createIcon(Store01Icon),
  marketApplicant: createIcon(Store01Icon),
  candidate: createIcon(UserSearch01Icon),
  report: createIcon(File01Icon),
} as const

export type NavigationIcon = (typeof NAVIGATION_ICONS)[keyof typeof NAVIGATION_ICONS]
